// Analytics — GTM is installed once, statically, in public/index.html
// (container GTM-MNH9HHKX) so it ships in every prerendered page's <head>.
// Marketing attribution capture/storage lives in ./attribution.js (first-party,
// versioned, 90-day). The data layer receives ONLY analytics-safe values:
// no PII (name/email/phone/company/designation/address/free-text) and no
// advertising click identifiers (gclid/gbraid/wbraid/msclkid/fbclid).
// GA4/Ads gtags below load ONLY when env IDs are configured (staging default: none).
// Required environment variables (owner supplies when needed):
//   REACT_APP_GA4_ID          — GA4 measurement ID (G-XXXXXXX)
//   REACT_APP_GOOGLE_ADS_ID   — Google Ads tag (AW-XXXXXXX) for offline conversions
import { getAnalyticsAttribution } from "./attribution";

// events that automatically carry analytics-safe attribution + page_path
// (spec Parts 7 & 9 — micro-conversions for GA4/Clarity via GTM)
const SAFE_EVENTS = new Set([
  "survey_cta_click",
  "survey_form_start",
  "survey_form_step",
  "phone_click",
  "whatsapp_click",
]);

// push a marketing/UX event to the data layer
export function track(event, params = {}) {
  window.dataLayer = window.dataLayer || [];
  const payload = { event, ...params };
  if (SAFE_EVENTS.has(event)) {
    payload.page_path = params.page_path || window.location.pathname; // path only — never query strings
    payload.attribution = params.attribution || getAnalyticsAttribution();
  }
  window.dataLayer.push(payload);
}

// One delegated listener covers every placement of the conversion actions —
// tel:, mailto:, wa.me and /request-survey links — without per-component wiring.
function initClickTracking() {
  document.addEventListener("click", (e) => {
    const a = e.target && e.target.closest ? e.target.closest("a") : null;
    if (!a) return;
    const href = a.getAttribute("href") || "";
    const placement = a.getAttribute("data-testid") || "unknown";
    const path = href.split("?")[0];
    if (href.startsWith("tel:")) track("phone_click", { placement });
    else if (href.startsWith("mailto:")) track("email_clicked", { placement });
    else if (/wa\.me|whatsapp/i.test(href)) track("whatsapp_click", { placement });
    else if (path.startsWith("/request-survey")) track("survey_cta_click", { form_name: "request_free_site_survey", service: "", placement });
    else if (/^\/services\/[^/]+/.test(path)) track("service_selected", { service: path.split("/")[2], placement });
    else if (/^\/our-work\/[^/]+/.test(path)) track("case_study_viewed", { project: path.split("/")[2], placement });
    else if (/^\/locations\/[^/]+/.test(path)) track("location_selected", { location: path.split("/")[2], placement });
  }, { capture: true });
}

export function initAnalytics() {
  if (window.__dccAnalyticsInit) return;
  window.__dccAnalyticsInit = true;
  window.dataLayer = window.dataLayer || [];

  initClickTracking();

  // GTM is inlined in public/index.html — never inject it here (single load).
  const ga4 = process.env.REACT_APP_GA4_ID;
  const ads = process.env.REACT_APP_GOOGLE_ADS_ID;

  if (ga4 || ads) {
    const id = ga4 || ads;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(s);
    window.gtag = function gtag(){ window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    if (ga4) window.gtag("config", ga4);
    if (ads) window.gtag("config", ads);
  }
}
